import * as React from 'react';
import { ApolloError } from 'apollo-client';
import { AdminUpgradeSharedQuote_sharedQuote_quoteData } from 'admin/queries/__generated__/AdminUpgradeSharedQuote';
// @ts-ignore
import AdminUpgradeInformationalPage from 'admin/components/AdminUpgradeInformationalPage';
import AdminUpgradePlanSharedQuoteFound from 'admin/components/AdminUpgradePlan/AdminUpgradePlanSharedQuoteFound';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import EventTracking from 'lib/EventTracking';
import Link from 'components/Link';

type QuoteData = AdminUpgradeSharedQuote_sharedQuote_quoteData;

type Props = {
  quote?: QuoteData;
  quoteState: string;
  loading: boolean;
  error?: ApolloError;
};

type State = {
  taxExempt: boolean;
};

class AdminUpgradePlanSharedQuotePage extends React.Component<Props, State> {
  componentDidMount() {
    const { quote, quoteState } = this.props;

    if (quote && quoteState) {
      this.trackViewEvents(quoteState);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { quote, quoteState } = this.props;
    const { quote: prevQuote } = prevProps;

    if (quote && quoteState && !prevQuote) {
      this.trackViewEvents(quoteState);
    }
  }

  track = (prefix: string, key: string) => {
    EventTracking.trackKey(`${prefix}.${key}`);
  };

  trackViewEvents = (quoteState: string) => {
    if (quoteState === 'expired') {
      this.track('admin_self_serve.shared_quote_expired', 'view');
    } else if (quoteState === 'already_sent') {
      this.track('admin_self_serve.shared_quote_completed', 'view');
    } else if (quoteState !== 'found') {
      // tracking for 'found' is in AdminUpgradePlanSharedQuoteFound
      this.track('admin_self_serve.shared_quote_not_found', 'view');
    }
  };

  handleTaxExemptChange = (taxExempt: boolean) => {
    this.setState({ taxExempt });
  };

  renderQuote = () => {
    const { quote } = this.props;

    if (quote) {
      this.setState({ taxExempt: quote.taxExempt });

      return (
        <AdminUpgradePlanSharedQuoteFound
          quote={quote}
          taxExempt={this.state.taxExempt}
          onTaxExemptChange={this.handleTaxExemptChange}
        />
      );
    }
  };

  renderQuoteExpired = () => {
    return (
      <AdminUpgradeInformationalPage
        title="The quote you're looking for has expired"
        subtext="Remind School and District Plan quotes expire after 60 days."
        action={
          <Button
            to="/modified_admin_upgrade/"
            onClick={() =>
              this.track('admin_self_serve.shared_quote_expired', 'continue.click')
            }>
            Get a new quote
          </Button>
        }
      />
    );
  }

  renderQuoteAlreadySent = () => {
    return (
      <AdminUpgradeInformationalPage
        title="This purchase has already been completed"
        subtext={
          <span>
            Have questions about what’s next? Email our School Success team for help at{' '}
            <Link href="mailto:schoolplan@remindhq.com">schoolplan@remindhq.com</Link>.
          </span>
        }
        action={
          <Button
            onClick={() =>
              this.track('admin_self_serve.shared_quote_completed', 'continue.click')
            }
            to="/">
            Go to Remind
          </Button>
        }
      />
    );
  }

  renderQuoteNotFound = () => {
    return (
      <AdminUpgradeInformationalPage
        title="We couldn't find the quote you're looking for"
        subtext="It'll only take a minute to get a quote for your school or district."
        action={
          <Button
            onClick={() =>
              this.track('admin_self_serve.shared_quote_not_found', 'continue.click')
            }
            to="/modified_admin_upgrade/">
            Get a quote
          </Button>
        }
      />
    );
  }

  render() {
    const { loading, quote, quoteState, error } = this.props;

    if (error) {
      console.log('Unable to render AdminUpgradePlanSharedQuotePage', error);
      return this.renderQuoteNotFound();
    }

    if (loading) {
      return <CenteredLoadingIndicator data-test="loading" />;
    }

    if (quote && quoteState === 'found') {
      return this.renderQuote();
    } else if (quote && quoteState === 'expired') {
      return this.renderQuoteExpired();
    } else if (quote && quoteState === 'already_sent') {
      return this.renderQuoteAlreadySent();
    }
    return this.renderQuoteNotFound();
  }
}

export { AdminUpgradePlanSharedQuotePage };
export default AdminUpgradePlanSharedQuotePage;
